/**
 * Cold-start concierge intake — turn a tablet brief/job into a human-ops message
 * for WhatsApp / Telegram. No server relay; client deep-links only.
 */

import { PRODUCT_BRAND } from '../config/productBrand';
import { getHelpChannelLinks } from './communityChatIntent';

const clip = (s, n = 120) => {
  const t = String(s || '').trim();
  if (!t) return '';
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
};

/**
 * Build a moderator-ready ask message from a job or brief.
 * @param {object} opts
 * @param {string} [opts.appId]
 * @param {object} [opts.job]
 * @param {object} [opts.brief]
 * @param {string} [opts.title]
 */
export const buildConciergeMessage = ({ appId, job, brief, title } = {}) => {
  const b = brief || job?.brief || {};
  const lines = [
    `Hi ${PRODUCT_BRAND.name} ops — concierge match request.`,
    `App: ${appId || job?.appId || 'services'}`,
  ];

  const jobTitle = title || job?.title || b.itemTitle || b.serviceNeed || b.jobTitle;
  if (jobTitle) {
    lines.push(`Need: ${clip(jobTitle, 160)}`);
  }
  if (job?.id) {
    lines.push(`Job id: ${job.id}`);
  }
  if (job?.status) {
    lines.push(`Status: ${job.status}`);
  }

  const category = b.categoryId || b.category || b.serviceCategory;
  if (category) {
    lines.push(`Category: ${category}`);
  }

  const area = b.area || b.location || b.zone || b.pickupArea;
  if (area) {
    lines.push(`Area: ${clip(area, 80)}`);
  }

  const budget = b.budget || b.cashBudget || b.escrowAmount || b.deposit;
  if (budget) {
    lines.push(`Budget: ${budget}${b.currency ? ` ${b.currency}` : ''}`);
  }

  const when = b.scheduleWindow || job?.scheduleWindow || b.timeWindow || b.whenNeeded;
  if (when) {
    lines.push(`When: ${clip(when, 80)}`);
  }

  const notes = b.notes || b.description || b.details || b.scope;
  if (notes) {
    lines.push(`Notes: ${clip(notes, 200)}`);
  }

  lines.push('Please help find a provider/runner — I can wait for a human match.');
  return lines.join('\n');
};

/**
 * Help channel links with concierge prefill (WhatsApp). Telegram opens community URL.
 */
export const getConciergeLinks = ({ appId, job, brief, title } = {}) => {
  const text = buildConciergeMessage({ appId, job, brief, title });
  return {
    text,
    links: getHelpChannelLinks({ appId: appId || job?.appId, text }),
  };
};

/**
 * Copy concierge text then open Telegram (groups cannot prefill reliably).
 * @returns {Promise<boolean>} true if clipboard write succeeded
 */
export const copyConciergeTextAndOpen = async (href, text) => {
  let copied = false;
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText && text) {
    try {
      await navigator.clipboard.writeText(text);
      copied = true;
    } catch {
      copied = false;
    }
  }
  if (typeof window !== 'undefined' && href) {
    window.open(href, '_blank', 'noopener,noreferrer');
  }
  return copied;
};
